import React, { useMemo, useState } from 'react';
import { FileText, Download, Share2, Printer, Calendar, Filter, Search, ChevronDown, X, Clock, FilePlus, MoreVertical, Sparkles, User, Users, LayoutDashboard } from 'lucide-react';

// --- Types ---
type ReportType = 'Individual' | 'Team Aggregate' | 'Executive Summary';
type TabId = 'overview' | 'current' | 'historical';
interface Report {
  id: string;
  title: string;
  type: ReportType;
  cycle: string;
  date: string;
  size: string;
  competencies: string[];
}

// --- Mock Data ---
const ALL_COMPETENCIES = ['Strategic Thinking', 'Communication', 'Execution', 'Team Development', 'Emotional Intelligence', 'Accountability', 'Risk', 'Collaboration'];
const MOCK_REPORTS: Report[] = [
// Current (Q3)
{
  id: 'rep_1',
  title: 'Sarah Connor — 360° Leadership Report',
  type: 'Individual',
  cycle: 'Q3 Leadership Review',
  date: 'Oct 15, 2023',
  size: '2.4 MB',
  competencies: ['Strategic Thinking', 'Execution', 'Risk']
}, {
  id: 'rep_2',
  title: 'Michael Scott — 360° Leadership Report',
  type: 'Individual',
  cycle: 'Q3 Leadership Review',
  date: 'Oct 14, 2023',
  size: '2.1 MB',
  competencies: ['Communication', 'Emotional Intelligence']
}, {
  id: 'rep_3',
  title: 'Jim Halpert — 360° Leadership Report',
  type: 'Individual',
  cycle: 'Q3 Leadership Review',
  date: 'Oct 12, 2023',
  size: '1.9 MB',
  competencies: ['Collaboration', 'Communication']
}, {
  id: 'rep_4',
  title: 'Jane Doe — 360° Leadership Report',
  type: 'Individual',
  cycle: 'Q3 Leadership Review',
  date: 'Oct 10, 2023',
  size: '2.5 MB',
  competencies: ['Strategic Thinking', 'Team Development', 'Accountability']
}, {
  id: 'rep_5',
  title: 'Engineering Dept — Aggregate Insights',
  type: 'Team Aggregate',
  cycle: 'Q3 Leadership Review',
  date: 'Oct 16, 2023',
  size: '4.2 MB',
  competencies: ['Execution', 'Collaboration', 'Risk']
}, {
  id: 'rep_6',
  title: 'Q3 Executive Summary',
  type: 'Executive Summary',
  cycle: 'Q3 Leadership Review',
  date: 'Oct 18, 2023',
  size: '5.1 MB',
  competencies: ['Strategic Thinking', 'Execution', 'Team Development']
},
// Historical (Q2, Q1)
{
  id: 'rep_7',
  title: 'Sarah Connor — 360° Leadership Report',
  type: 'Individual',
  cycle: 'Q2 Leadership Review',
  date: 'Jul 12, 2023',
  size: '2.3 MB',
  competencies: ['Strategic Thinking', 'Execution']
}, {
  id: 'rep_8',
  title: 'Jane Doe — 360° Leadership Report',
  type: 'Individual',
  cycle: 'Q2 Leadership Review',
  date: 'Jul 10, 2023',
  size: '2.4 MB',
  competencies: ['Team Development', 'Accountability']
}, {
  id: 'rep_9',
  title: 'Q2 Executive Summary',
  type: 'Executive Summary',
  cycle: 'Q2 Leadership Review',
  date: 'Jul 20, 2023',
  size: '4.8 MB',
  competencies: ['Strategic Thinking', 'Execution']
}, {
  id: 'rep_10',
  title: 'Michael Scott — 360° Leadership Report',
  type: 'Individual',
  cycle: 'Q1 Leadership Review',
  date: 'Apr 05, 2023',
  size: '2.0 MB',
  competencies: ['Communication']
}];
const CURRENT_CYCLE = 'Q3 Leadership Review';
export function Reports() {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('All Time');
  const [selectedCompetencies, setSelectedCompetencies] = useState<string[]>([]);
  const [showCompetencyFilters, setShowCompetencyFilters] = useState(false);
  const isLibraryTab = activeTab === 'current' || activeTab === 'historical';
  const filteredReports = useMemo(() => {
    if (!isLibraryTab) return [];
    return MOCK_REPORTS.filter(report => {
      const isCurrent = report.cycle === CURRENT_CYCLE;
      if (activeTab === 'current' && !isCurrent) return false;
      if (activeTab === 'historical' && isCurrent) return false;
      if (searchQuery && !report.title.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      if (selectedCompetencies.length > 0) {
        const hasMatchingCompetency = report.competencies.some(c => selectedCompetencies.includes(c));
        if (!hasMatchingCompetency) return false;
      }
      return true;
    });
  }, [activeTab, searchQuery, selectedCompetencies, dateRange, isLibraryTab]);
  const currentCount = MOCK_REPORTS.filter(r => r.cycle === CURRENT_CYCLE).length;
  const toggleCompetency = (comp: string) => {
    setSelectedCompetencies(prev => prev.includes(comp) ? prev.filter(c => c !== comp) : [...prev, comp]);
  };
  const clearFilters = () => {
    setSelectedCompetencies([]);
    setSearchQuery('');
    setDateRange('All Time');
  };
  const activeFilterCount = selectedCompetencies.length + (dateRange !== 'All Time' ? 1 : 0);
  const tabs: {
    id: TabId;
    label: string;
  }[] = [{
    id: 'overview',
    label: 'Overview'
  }, {
    id: 'current',
    label: 'Current Cycle'
  }, {
    id: 'historical',
    label: 'Historical Archive'
  }];
  return <div className="space-y-6 animate-in fade-in duration-500 pb-12" data-id="element-2071" data-mp="87wj2qopa7k0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4" data-id="element-2072" data-mp="38tgre03uffq">
        <div data-id="element-2073" data-mp="3ugfp3jxrr62x">
          <h1 className="text-2xl font-semibold text-slate-900" data-id="element-2074" data-mp="3ugz9r8et08kn">
            Reports & Insights
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-2075" data-mp="3pnwe8uqgup1t">
            Review performance insights and manage generated assessment
            reports.
          </p>
        </div>
        <div className="flex space-x-3" data-id="element-2076" data-mp="3gpkdhzvvyavn">
          <button className="border border-slate-200 text-slate-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors flex items-center bg-white" data-id="element-2077" data-mp="3gqm2775tqvvu">
            <Download className="h-4 w-4 mr-2" data-id="element-2078" data-mp="36rmtxpoz129d" />
            Export All
          </button>
          <button className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center" data-id="element-2079" data-mp="2x1xzbvfu1lsi">
            <FilePlus className="h-4 w-4 mr-2" data-id="element-2080" data-mp="31zpr8pgh97wp" />
            Generate Report
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200" data-id="element-2081" data-mp="2s34lg1wmvsrt">
        <nav className="-mb-px flex space-x-8" aria-label="Report views" data-id="element-2082" data-mp="2s5z5vtfnddl8">
          {tabs.map(tab => {
          const isActive = activeTab === tab.id;
          return <button key={tab.id} onClick={() => setActiveTab(tab.id)} aria-current={isActive ? 'page' : undefined} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center ${isActive ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`} data-id="element-2083" data-mp="3ks24i78o5tlm">
                {tab.id === 'overview' && <LayoutDashboard className="h-4 w-4 mr-2" data-id="element-2084" data-mp="1s2rnzvj0b4q5" />}
                {tab.id === 'historical' && <Clock className="h-4 w-4 mr-2" data-id="element-2085" data-mp="1n47wlfln90qe" />}
                {tab.label}
                {tab.id === 'current' && <span className={`ml-2 py-0.5 px-2.5 rounded-full text-xs ${isActive ? 'bg-slate-100 text-slate-900' : 'bg-slate-100 text-slate-500'}`} data-id="element-2086" data-mp="21vyob1yw3ujd">
                    {currentCount}
                  </span>}
              </button>;
        })}
        </nav>
      </div>

      {activeTab === 'overview' ? <OverviewPanel onBrowseReports={() => setActiveTab('current')} data-id="element-2087" data-mp="1j4z56he2tey0" /> : <>
          {/* Filter Bar */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-4" data-id="element-2088" data-mp="1e6nxodb97giy">
            <div className="flex flex-col md:flex-row gap-4" data-id="element-2089" data-mp="2uke3boi0ucup">
              <div className="relative flex-1" data-id="element-2090" data-mp="2pioqq4f5hr4i">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-2091" data-mp="mwt6rbt49z7b" />
                <input type="text" placeholder="Search reports by name or type..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-slate-100 outline-none transition-all" data-id="element-2092" data-mp="muyhxmeq5mxy" />
              </div>

              <div className="relative min-w-[200px]" data-id="element-2093" data-mp="1akstm0nb55gm">
                <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-2094" data-mp="1flqdqirvo9kr" />
                <select value={dateRange} onChange={e => setDateRange(e.target.value)} aria-label="Date range" className="w-full pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-slate-100 outline-none transition-all appearance-none cursor-pointer" data-id="element-2095" data-mp="1kkjnc041ngfn">
                  <option value="All Time" data-id="element-2096" data-mp="3a1f91ys9k28y">All Time</option>
                  <option value="Last 30 Days" data-id="element-2097" data-mp="39zufabdrwnc5">Last 30 Days</option>
                  <option value="This Quarter" data-id="element-2098" data-mp="3533z7o1yxrqq">This Quarter</option>
                  <option value="Last Quarter" data-id="element-2099" data-mp="304akjsay3uoz">Last Quarter</option>
                  <option value="This Year" data-id="element-2100" data-mp="301ogi2n9jxbi">This Year</option>
                </select>
                <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-slate-400 pointer-events-none" data-id="element-2101" data-mp="1jsu2rxh3cuz" />
              </div>

              <button onClick={() => setShowCompetencyFilters(!showCompetencyFilters)} aria-expanded={showCompetencyFilters} className={`flex items-center px-4 py-2 border rounded-md text-sm font-medium transition-colors ${showCompetencyFilters || selectedCompetencies.length > 0 ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`} data-id="element-2102" data-mp="3nsttma1tkbi4">
                <Filter className="h-4 w-4 mr-2" data-id="element-2103" data-mp="2gorxilze2m5z" />
                Competencies
                {selectedCompetencies.length > 0 && <span className="ml-2 bg-indigo-100 text-indigo-700 py-0.5 px-2 rounded-full text-xs" data-id="element-2104" data-mp="2qe1ofyihxysi">
                    {selectedCompetencies.length}
                  </span>}
              </button>
            </div>

            {showCompetencyFilters && <div className="pt-4 border-t border-slate-100 animate-in slide-in-from-top-2 duration-200" data-id="element-2105" data-mp="2zgufq1roiaic">
                <div className="flex items-center justify-between mb-3" data-id="element-2106" data-mp="39d5r8pjg2wbq">
                  <span className="text-sm font-medium text-slate-700" data-id="element-2107" data-mp="39b35ihpbrpct">
                    Filter by Competency
                  </span>
                  {activeFilterCount > 0 && <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-900 flex items-center transition-colors" data-id="element-2108" data-mp="1ezobvpt715rs">
                      <X className="h-3 w-3 mr-1" data-id="element-2109" data-mp="3lo6sqxqr98t1" />
                      Clear filters
                    </button>}
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-2110" data-mp="3bydiq169h1qw">
                  {ALL_COMPETENCIES.map(comp => {
              const isSelected = selectedCompetencies.includes(comp);
              return <button key={comp} onClick={() => toggleCompetency(comp)} aria-pressed={isSelected} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${isSelected ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'}`} data-id="element-2111" data-mp="32w4bpla7bqi9">
                        {comp}
                      </button>;
            })}
                </div>
              </div>}
          </div>

          {/* Report List */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-2112" data-mp="pqh39ar5vejk">
            {filteredReports.length === 0 ? <div className="p-12 text-center" data-id="element-2113" data-mp="krmnkhohq63o">
                <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2114" data-mp="gpsrynt5jsad">
                  <FileText className="h-6 w-6 text-slate-400" data-id="element-2115" data-mp="1dg1pq3miwtxl" />
                </div>
                <h3 className="text-sm font-medium text-slate-900 mb-1" data-id="element-2116" data-mp="18gys348vhpzn">
                  No reports found
                </h3>
                <p className="text-sm text-slate-500" data-id="element-2117" data-mp="13oxdvcs59p6z">
                  Try adjusting your search or filter criteria.
                </p>
                {activeFilterCount > 0 && <button onClick={clearFilters} className="mt-4 text-sm text-indigo-600 font-medium hover:text-indigo-800" data-id="element-2118" data-mp="1w6jdruwev1cm">
                    Clear all filters
                  </button>}
              </div> : <div className="divide-y divide-slate-100" data-id="element-2119" data-mp="3f0mcmy3wz8c8">
                {filteredReports.map(report => <ReportRow key={report.id} report={report} data-id="element-2120" data-mp="3j27tsvnhe8s8" />)}
              </div>}
          </div>
        </>}
    </div>;
}
function OverviewPanel({
  onBrowseReports
}: {
  onBrowseReports: () => void;
}) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2121" data-mp="1uvz2bfrkrkq">
      {/* AI Executive Summary */}
      <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-6 shadow-sm col-span-1 md:col-span-2" data-id="element-2122" data-mp="6lkpdnfpjiia">
        <div className="flex items-center mb-4" data-id="element-2123" data-mp="amxutwew1joo">
          <div className="bg-indigo-100 p-2 rounded-lg mr-3" data-id="element-2124" data-mp="amo60c5kxikk">
            <Sparkles className="h-5 w-5 text-indigo-600" data-id="element-2125" data-mp="fmskjwmxg385" />
          </div>
          <h2 className="text-lg font-semibold text-indigo-900" data-id="element-2126" data-mp="kldddazunkmk">
            AI Executive Summary
          </h2>
        </div>
        <div className="space-y-3 text-indigo-900/80 text-sm leading-relaxed" data-id="element-2127" data-mp="6xv4ras5ntoy">
          <p data-id="element-2128" data-mp="1ztkimiztobw">
            Across the <strong data-id="element-2129" data-mp="1yiiar574omw">{CURRENT_CYCLE}</strong>, the organization shows
            strong performance in <strong data-id="element-2130" data-mp="gopreg9bcvax">Strategic Thinking</strong> (avg
            4.2/5) but indicates a blind spot in{' '}
            <strong data-id="element-2131" data-mp="bp3gwekvdt5t">Cross-functional Communication</strong> (avg 3.1/5).
          </p>
          <p data-id="element-2132" data-mp="3k4n4oy2rujdh">
            <strong data-id="element-2133" data-mp="3k67y2kdcnw9r">Key Insight:</strong> Managers consistently rate themselves
            higher in empathy than their direct reports do. Consider targeted
            coaching on active listening for middle management.
          </p>
        </div>
      </div>

      {/* Individual Reports */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-2134" data-mp="3p400477pkyro">
        <div className="p-5 border-b border-slate-200 flex justify-between items-center bg-slate-50" data-id="element-2135" data-mp="19clty1lkdhfm">
          <div className="flex items-center" data-id="element-2136" data-mp="14etummilat1e">
            <User className="h-5 w-5 text-slate-500 mr-2" data-id="element-2137" data-mp="14g4y0l84hyrz" />
            <h3 className="font-medium text-slate-900" data-id="element-2138" data-mp="1bwi8amt1p2r0">Individual Reports</h3>
          </div>
          <span className="text-xs bg-slate-200 text-slate-700 px-2 py-1 rounded-full font-medium" data-id="element-2139" data-mp="1gwwgq5yk6epk">
            12 Ready
          </span>
        </div>
        <div className="divide-y divide-slate-100" data-id="element-2140" data-mp="12yy352s2amb4">
          {[{
          name: 'Sarah Connor',
          role: 'Director of Engineering',
          score: '4.6'
        }, {
          name: 'Michael Scott',
          role: 'Regional Manager',
          score: '3.8'
        }, {
          name: 'Jim Halpert',
          role: 'Sales Lead',
          score: '4.2'
        }].map((person, i) => <button key={i} onClick={onBrowseReports} className="w-full text-left p-4 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-2141" data-mp="1puyemcmzwr8m" data-mpi={i}>
              <div className="flex items-center" data-id="element-2142" data-mp="2s9lzxseboi6e">
                <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600 mr-3" data-id="element-2143" data-mp="2s8b186zqi0m5">
                  {person.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div data-id="element-2144" data-mp="2e91h4w8eqmcw">
                  <div className="text-sm font-medium text-slate-900" data-id="element-2145" data-mp="2e9l3cp53bwwq">
                    {person.name}
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-2146" data-mp="24k1ixf1qbuzr">{person.role}</div>
                </div>
              </div>
              <div className="flex items-center space-x-4" data-id="element-2147" data-mp="20hwrmmyr7aga">
                <div className="text-right" data-id="element-2148" data-mp="20gbxgygcptiy">
                  <div className="text-sm font-semibold text-slate-900" data-id="element-2149" data-mp="1vk4osxwkm3xb">
                    {person.score}
                  </div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-2150" data-mp="1qjqroawicee2">
                    Avg Score
                  </div>
                </div>
                <ChevronDown className="h-4 w-4 text-slate-400 -rotate-90" data-id="element-2151" data-mp="1bi0cna5u7798" />
              </div>
            </button>)}
        </div>
        <div className="p-3 border-t border-slate-200 bg-slate-50 text-center" data-id="element-2152" data-mp="1geh1k6kv1pnd">
          <button onClick={onBrowseReports} className="text-sm text-slate-600 font-medium hover:text-slate-900" data-id="element-2153" data-mp="1lgpux41natq0">
            View All 12 Reports
          </button>
        </div>
      </div>

      {/* Team Aggregates */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-2154" data-mp="xpkhswn3afje">
        <div className="p-5 border-b border-slate-200 flex justify-between items-center bg-slate-50" data-id="element-2155" data-mp="12ox49ga1tklg">
          <div className="flex items-center" data-id="element-2156" data-mp="17fdgix24v068">
            <Users className="h-5 w-5 text-slate-500 mr-2" data-id="element-2157" data-mp="17gf1pucv531o" />
            <h3 className="font-medium text-slate-900" data-id="element-2158" data-mp="a0rhyp3cjnji">Team Aggregates</h3>
          </div>
        </div>
        <div className="p-6 space-y-6" data-id="element-2159" data-mp="eysyp3davart">
          {[{
          label: 'Strategic Thinking',
          score: '4.2',
          width: '84%',
          bar: 'bg-emerald-500'
        }, {
          label: 'Execution',
          score: '3.9',
          width: '78%',
          bar: 'bg-blue-500'
        }, {
          label: 'Communication',
          score: '3.1',
          width: '62%',
          bar: 'bg-amber-500'
        }].map(item => <div key={item.label} data-id="element-2160" data-mp="2c2kbh6jt7ka8">
              <div className="flex justify-between text-sm mb-1" data-id="element-2161" data-mp="283lb7z90wbp4">
                <span className="font-medium text-slate-700" data-id="element-2162" data-mp="2818wrw2h4jgt">{item.label}</span>
                <span className="text-slate-900 font-semibold" data-id="element-2163" data-mp="2ltosebt1pf7p">
                  {item.score}
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-2164" data-mp="2vsntn9bhe8ko">
                <div className={`${item.bar} h-2 rounded-full`} style={{
              width: item.width
            }} data-id="element-2165" data-mp="2vqjpcq9o77jv"></div>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}
function ReportRow({
  report
}: {
  report: Report;
}) {
  const isExecutive = report.type === 'Executive Summary';
  const isAggregate = report.type === 'Team Aggregate';
  return <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between hover:bg-slate-50 transition-colors group" data-id="element-2166" data-mp="35fgt1n05pegs">
      <div className="flex items-start mb-4 sm:mb-0" data-id="element-2167" data-mp="2qq2kgh4en12g">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 mr-4 ${isExecutive ? 'bg-indigo-50 text-indigo-600' : isAggregate ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`} data-id="element-2168" data-mp="2vilvb6bpji3k">
          <FileText className="h-5 w-5" data-id="element-2169" data-mp="2mq0is7yo67n6" />
        </div>
        <div data-id="element-2170" data-mp="27rzthwsaqu20">
          <h4 className="text-sm font-semibold text-slate-900 line-clamp-1" data-id="element-2171" data-mp="27tj8zl64p12q">
            {report.title}
          </h4>
          <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs text-slate-500" data-id="element-2172" data-mp="2cr2yjh8a109z">
            <span className={`px-2 py-0.5 rounded-full font-medium ${isExecutive ? 'bg-indigo-100 text-indigo-700' : isAggregate ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`} data-id="element-2173" data-mp="2bve2vw1n0pmb">
              {report.type}
            </span>
            <span className="hidden sm:inline text-slate-300" data-id="element-2174" data-mp="26wb0ykgyezik">•</span>
            <span data-id="element-2175" data-mp="2umot0azjgyd5">{report.cycle}</span>
            <span className="hidden sm:inline text-slate-300" data-id="element-2176" data-mp="2unq7yqjoe505">•</span>
            <span data-id="element-2177" data-mp="2zli8g8ydyzfy">{report.date}</span>
            <span className="hidden sm:inline text-slate-300" data-id="element-2178" data-mp="2zkgmvajquujz">•</span>
            <span data-id="element-2179" data-mp="2kxf2v0o99pro">{report.size}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-2 sm:opacity-0 sm:group-hover:opacity-100 focus-within:opacity-100 transition-opacity self-end sm:self-auto" data-id="element-2180" data-mp="2pvynmxr44vig">
        <button aria-label={`Share ${report.title}`} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors" title="Share" data-id="element-2181" data-mp="3djzum28n83vr">
          <Share2 className="h-4 w-4" data-id="element-2182" data-mp="16vfz2u6igqxw" />
        </button>
        <button aria-label={`Print ${report.title}`} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors" title="Print" data-id="element-2183" data-mp="11yz9rwnks6j8">
          <Printer className="h-4 w-4" data-id="element-2184" data-mp="1bp20fq7858ee" />
        </button>
        <button aria-label={`Download ${report.title}`} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors" title="Download" data-id="element-2185" data-mp="1bqd6xxs20u97">
          <Download className="h-4 w-4" data-id="element-2186" data-mp="wyusw5ir99sh" />
        </button>
        <div className="w-px h-4 bg-slate-200 mx-1 hidden sm:block" data-id="element-2187" data-mp="x0flvqpf8ko8"></div>
        <button aria-label={`More options for ${report.title}`} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors hidden sm:block" data-id="element-2188" data-mp="swg6xcjshtwz">
          <MoreVertical className="h-4 w-4" data-id="element-2189" data-mp="24bwlao7ye9se" />
        </button>
      </div>
    </div>;
}